import type { KeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api'
import type { CompanyHit } from '../lib/types'
import { SearchIcon } from './icons'
import styles from './SearchBox.module.css'

/**
 * Header search (WAI-ARIA combobox): suggests company symbols/names from
 * company_master as you type (top 8). Picking one opens /stock/:symbol; Enter
 * with nothing selected searches announcements for the typed words.
 */
export function SearchBox({ label = 'Search stocks or filings', enabled = true }: { label?: string; enabled?: boolean }) {
  const navigate = useNavigate()
  const id = useId()
  const listId = `${id}-list`
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<CompanyHit[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const seq = useRef(0)

  useEffect(() => {
    const term = q.trim()
    if (!enabled || term.length < 1) { setHits([]); return }
    const mine = ++seq.current
    const t = setTimeout(() => {
      api<{ data: CompanyHit[] }>(`/companies/search?q=${encodeURIComponent(term)}&limit=8`)
        .then(r => { if (mine === seq.current) { setHits(r.data); setOpen(true); setActive(-1) } })
        .catch(() => { if (mine === seq.current) setHits([]) })
    }, 150)
    return () => clearTimeout(t)
  }, [q, enabled])

  const go = (hit?: CompanyHit) => {
    setOpen(false)
    if (hit) { setQ(''); navigate(`/stock/${encodeURIComponent(hit.symbol)}`); return }
    const term = q.trim()
    navigate(term ? `/announcements?q=${encodeURIComponent(term)}` : '/announcements')
  }

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(hits.length - 1, a + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(-1, a - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); go(active >= 0 ? hits[active] : undefined) }
    else if (e.key === 'Escape') { setOpen(false); setActive(-1) }
  }

  const expanded = open && hits.length > 0
  return (
    <div className={styles.wrap}>
      <div className={styles.box}>
        <SearchIcon size={16} />
        <label htmlFor={id} className="sr-only">{label}</label>
        <input
          id={id}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? `${id}-opt-${active}` : undefined}
          autoComplete="off"
          placeholder="Search stocks, filings…"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => hits.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
      </div>
      <ul id={listId} role="listbox" aria-label="Matching companies" className={styles.list} hidden={!expanded}>
        {hits.map((h, i) => (
          <li
            key={h.symbol}
            id={`${id}-opt-${i}`}
            role="option"
            aria-selected={i === active}
            className={`${styles.opt} ${i === active ? styles.active : ''}`}
            onMouseDown={e => { e.preventDefault(); go(h) }}
            onMouseEnter={() => setActive(i)}
          >
            <span className={styles.sym}>{h.symbol}</span>
            <span className={styles.name}>{h.name}</span>
          </li>
        ))}
        <li role="option" aria-selected={false} className={styles.hint} onMouseDown={e => { e.preventDefault(); go() }}>
          Press Enter to search filings for “{q.trim()}”
        </li>
      </ul>
    </div>
  )
}
